import { dojoRecommendationExplanation } from '@/lib/dojoRecommendation';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHowieDojoWebSocket } from '@/hooks/useHowieDojoWebSocket';
import { HowieActionButton } from '@/components/HowieActionButton';
import { YourHowPanel, YourHowProgressHelp } from '@/components/unit/YourHowPanel';
import { PracticeRhythm } from '@/components/unit/PracticeRhythm';
import { createYourHowProgressPreview } from '@/lib/yourHowPreview';

export default function YourHowPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    catalog,
    profile,
    practiceProgress,
    isLoadingCatalog,
    catalogError,
    refreshCatalog,
  } = useHowieDojoWebSocket({ userId: user?.id });
  const isProgressPreview = import.meta.env.DEV
    && new URLSearchParams(window.location.search).get('preview') === 'progress';
  const effectivePracticeProgress = useMemo(
    () => (isProgressPreview ? createYourHowProgressPreview() : practiceProgress),
    [isProgressPreview, practiceProgress]
  );

  const recommendedUnit = useMemo(() => {
    const units = catalog?.units ?? [];
    return (
      units.find((unit) => unit.unit_id === catalog?.recommended_unit_id) ??
      units.find((unit) => unit.status === 'recommended') ??
      units.find((unit) => unit.status === 'in_progress') ??
      units.find((unit) => unit.status === 'available')
    );
  }, [catalog?.recommended_unit_id, catalog?.units]);

  const recommendedImperative = catalog?.imperatives.find(
    (imperative) => imperative.imperative_id === recommendedUnit?.imperative_id
  );

  return (
    <div
      className="dojo-scroll absolute inset-0 overflow-y-auto px-4 py-10 text-how-space-deep sm:px-8 sm:py-12 lg:py-14"
      style={{ background: 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)' }}
    >
      <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col">
        <header className="text-center">
          <h1 className="font-body text-[30px] font-medium leading-9 tracking-[-0.03em] text-how-space-deep sm:text-4xl sm:leading-tight">
            Your HOW
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-how-text-secondary/60">
            What your practice is revealing.
          </p>
        </header>

        <section className="pt-10">
          {catalogError && !isLoadingCatalog ? (
            <div className="rounded-2xl border border-red-100 bg-red-50/50 px-5 py-4">
              <p className="text-sm leading-6 text-red-600">Your HOW could not load.</p>
              <button
                type="button"
                onClick={() => void refreshCatalog()}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-100 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
              >
                <RotateCcw size={13} />
                Try again
              </button>
            </div>
          ) : isLoadingCatalog && !catalog ? (
            <p className="py-12 text-center text-sm text-how-text-secondary">Loading your HOW…</p>
          ) : (
            <YourHowPanel
              profile={profile}
              practiceProgress={effectivePracticeProgress}
              mastery={catalog?.mastery ?? null}
              showTitle={false}
            />
          )}
        </section>

        {recommendedUnit && (
          <div className="mx-auto mt-10 w-full max-w-[680px]">
            <section
              aria-labelledby="your-how-next-title"
              className="border-y border-how-space-deep/[0.08] py-5"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase leading-4 tracking-[0.08em] text-how-text-secondary/55">
                    Next for you
                  </p>
                  <h2
                    id="your-how-next-title"
                    className="mt-1 font-body text-base font-medium leading-5 tracking-[-0.015em] text-how-space-deep"
                  >
                    {recommendedUnit.title}
                  </h2>
                  <p className="mt-1.5 text-[13px] leading-5 text-how-text-secondary/65">
                    {recommendedImperative?.imperative_label ?? 'Recommended practice'}
                  </p>
                  <p className="mt-2 text-sm leading-5 text-how-text-secondary">
                    {dojoRecommendationExplanation(catalog?.recommended_reason, recommendedUnit.status === 'in_progress')}
                  </p>
                </div>

                <HowieActionButton
                  type="button"
                  onClick={() =>
                    navigate('/dojo', {
                      state: { startUnitId: recommendedUnit.unit_id },
                    })
                  }
                  className="self-start sm:self-auto"
                >
                  {recommendedUnit.status === 'in_progress' ? 'Continue' : 'Begin'}
                </HowieActionButton>
              </div>

              <button
                type="button"
                onClick={() =>
                  navigate('/dojo', {
                    state: { showPracticeLibrary: true },
                  })
                }
                className="mt-4 block text-[13px] text-how-text-secondary/55 underline decoration-how-text-secondary/25 underline-offset-4 transition-colors hover:text-how-space-deep hover:decoration-how-space-deep/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/40"
              >
                Browse all
              </button>
            </section>
          </div>
        )}

        <PracticeRhythm practiceProgress={effectivePracticeProgress} userId={user?.id} preview={isProgressPreview} />

        <footer className="mt-auto flex items-center justify-center pt-10 text-center text-[11px] leading-4 text-how-text-secondary/45">
          <YourHowProgressHelp />
        </footer>
      </div>
    </div>
  );
}
